import { useState, useEffect, useRef } from 'react';
import { API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, FileSpreadsheet, Upload, FileKey, Clock, Download, AlertTriangle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

export default function DataSafetyPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportPassword, setExportPassword] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showExportPw, setShowExportPw] = useState(false);

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPassword, setImportPassword] = useState('');
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [showImportPw, setShowImportPw] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStatus = () => {
    API.get('/backup/status')
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleExportSecure = async () => {
    if (!exportPassword || exportPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setExporting(true);
    try {
      const res = await API.post('/backup/export', { password: exportPassword }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename="')[1]?.replace('"', '') : 'backup.dat';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Encrypted backup downloaded');
      setExportPassword('');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await API.get('/backup/excel-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename="')[1]?.replace('"', '') : 'snapshot.xlsx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel snapshot downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDecodeInstructions = async () => {
    try {
      const res = await API.get('/backup/decode-instructions', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'YASH_GOLD_DECODE_INSTRUCTIONS.txt';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Decode instructions downloaded');
    } catch (e) {
      toast.error('Failed to download decode instructions');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.dat')) {
        toast.error('Please select a .dat backup file');
        return;
      }
      setImportFile(file);
      setImportPreview(null);
    }
  };

  const handleImportPreview = async () => {
    if (!importFile || !importPassword) {
      toast.error('Please select a file and enter the password');
      return;
    }
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = btoa(new Uint8Array(reader.result).reduce((d, b) => d + String.fromCharCode(b), ''));
        try {
          const res = await API.post('/backup/import', {
            file_data: base64,
            password: importPassword,
            mode: 'merge',
            dry_run: true,
          });
          setImportPreview(res.data.summary);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to preview backup');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsArrayBuffer(importFile);
    } catch (e) {
      toast.error('Failed to read file');
      setImporting(false);
    }
  };

  const handleImportApply = async (mode) => {
    if (!importFile || !importPassword) return;
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = btoa(new Uint8Array(reader.result).reduce((d, b) => d + String.fromCharCode(b), ''));
        try {
          const res = await API.post('/backup/import', {
            file_data: base64,
            password: importPassword,
            mode: mode,
          });
          toast.success(`Import complete: ${res.data.total_inserted} inserted, ${res.data.total_skipped} skipped`);
          setImportOpen(false);
          setImportFile(null);
          setImportPassword('');
          setImportPreview(null);
          fetchStatus();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Import failed');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsArrayBuffer(importFile);
    } catch (e) {
      toast.error('Failed to read file');
      setImporting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return 'Never';
    try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return d; }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="text-muted-foreground">Loading...</div></div>;

  return (
    <div data-testid="data-safety-page">
      <div className="page-header">
        <h1>Data Safety</h1>
        <p className="hidden sm:block text-sm text-muted-foreground">Backup, export, and restore your business data securely</p>
      </div>

      {/* Backup Status */}
      <Card className="mb-6" data-testid="backup-status-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock size={16} className="text-muted-foreground" /> Backup Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Last Export</p>
              <p className="font-semibold text-sm" data-testid="last-export-date">
                {status?.last_export ? formatDate(status.last_export.exported_at) : 'Never'}
              </p>
              {status?.last_export?.exported_by && (
                <p className="text-xs text-muted-foreground">by {status.last_export.exported_by}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Backup Period</p>
              <p className="font-semibold text-sm" data-testid="backup-period">
                {status?.current_period_start ? formatDate(status.current_period_start) : ''} — {status?.current_period_end ? formatDate(status.current_period_end) : ''}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Encrypted Backup */}
        <Card data-testid="encrypted-backup-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield size={16} className="text-amber-400" /> Encrypted Backup (.dat)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">AES-256 encrypted with password protection. Full database snapshot.</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Input
                  type={showExportPw ? 'text' : 'password'}
                  placeholder="Set backup password"
                  value={exportPassword}
                  onChange={e => setExportPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleExportSecure(); }}
                  data-testid="export-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowExportPw(!showExportPw)}
                  tabIndex={-1}
                >
                  {showExportPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleExportSecure}
              disabled={exporting || !exportPassword}
              className="gap-1.5 bg-amber-600 hover:bg-amber-700"
              data-testid="export-secure-btn"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export Secure Backup
            </Button>
          </CardContent>
        </Card>

        {/* Excel Snapshot */}
        <Card data-testid="excel-snapshot-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-emerald-400" /> Excel Snapshot (.xlsx)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Human-readable spreadsheet. One sheet per collection.</p>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="gap-1.5"
              data-testid="export-excel-btn"
            >
              {exportingExcel ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export Excel
            </Button>
          </CardContent>
        </Card>

        {/* Import Backup */}
        <Card data-testid="import-backup-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload size={16} className="text-blue-400" /> Import Backup (.dat)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Restore from encrypted backup. Preview before applying.</p>
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="gap-1.5"
              data-testid="import-backup-btn"
            >
              <Upload size={14} /> Import Backup
            </Button>
          </CardContent>
        </Card>

        {/* Decode Instructions */}
        <Card data-testid="decode-instructions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileKey size={16} className="text-purple-400" /> Decode Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Disaster recovery guide. Python script to decrypt without app.</p>
            <Button
              variant="outline"
              onClick={handleDecodeInstructions}
              className="gap-1.5"
              data-testid="decode-instructions-btn"
            >
              <Download size={14} /> Download Instructions
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Daily Backup Workflow */}
      <Card className="border-amber-800/30" data-testid="daily-workflow-card">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm mb-1">Daily Backup Workflow</p>
              <p className="text-sm text-muted-foreground">
                At night, click <strong>Export Secure Backup</strong>, save the .dat file in a local folder, and keep the password written separately. This ensures full recovery even if the server goes down.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Encrypted Backup</DialogTitle>
            <DialogDescription>Upload a .dat backup file and enter the password used during export.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Backup File (.dat)</Label>
              <div className="mt-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".dat"
                  onChange={handleFileSelect}
                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-border file:text-sm file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80 cursor-pointer"
                  data-testid="import-file-input"
                />
              </div>
              {importFile && <p className="text-xs text-muted-foreground mt-1">{importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</p>}
            </div>

            <div>
              <Label>Backup Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showImportPw ? 'text' : 'password'}
                  placeholder="Enter backup password"
                  value={importPassword}
                  onChange={e => setImportPassword(e.target.value)}
                  data-testid="import-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowImportPw(!showImportPw)}
                  tabIndex={-1}
                >
                  {showImportPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {!importPreview && (
              <Button onClick={handleImportPreview} disabled={importing || !importFile || !importPassword} className="gap-1.5" data-testid="import-preview-btn">
                {importing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                Preview Backup
              </Button>
            )}

            {importPreview && (
              <div className="space-y-3">
                <div className="rounded-lg border p-3 bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Backup Preview</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {Object.entries(importPreview.collections || {}).map(([col, info]) => (
                      <div key={col} className="flex justify-between">
                        <span className="font-mono">{col}</span>
                        <span className="font-mono font-semibold">{info.record_count} records</span>
                      </div>
                    ))}
                  </div>
                  {importPreview.manifest && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Period: {formatDate(importPreview.manifest.period_start_ist)} — {formatDate(importPreview.manifest.period_end_ist)}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => handleImportApply('merge')} disabled={importing} className="gap-1.5 flex-1" data-testid="import-merge-btn">
                    {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Merge (skip existing)
                  </Button>
                  <Button onClick={() => handleImportApply('replace_current_year_data')} disabled={importing} variant="destructive" className="gap-1.5 flex-1" data-testid="import-replace-btn">
                    {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Replace Year Data
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setImportOpen(false); setImportFile(null); setImportPassword(''); setImportPreview(null); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
